import React, { useEffect, useMemo, useState } from 'react'
import {
  FaFolderOpen as LibraryIcon,
  FaBroom as DedupeIcon,
  FaRegClone as DuplicateIcon,
} from 'react-icons/fa'
import { useSequencerStore } from '@/stores/sequencerStore'
import { apiClient } from '@/lib/api'
import { useEffectiveFeatures } from '@/hooks/useEffectiveFeatures'
import { ProjectLibrary } from './ProjectLibrary'

/**
 * Compact horizontal strip of pattern tabs with an add button. Lives
 * under the transport. Clicking a tab selects that pattern; double-
 * clicking enters rename mode. Delete is a hover affordance on the
 * active tab to avoid accidental clicks.
 *
 * Pattern cap enforcement (Pro+ users can have up to 16; free tier
 * capped at 1) is the SequencerPage's job — this component doesn't
 * gate. Pass `onLimitReached` to react to the cap hit.
 *
 * Note: the store still uses `project` keys (`projects`, `addProject`,
 * `selectProject`, etc.) to avoid a persisted-state migration — the
 * rename is UI-strings-only. A future PR will introduce a true
 * `SequencerProject` parent containing multiple `Pattern`s, and the
 * store keys will move at that point. See
 * plans/1-it-looks-like-synthetic-adleman.md item 5.
 */
interface ProjectBarProps {
  maxProjects: number
  onLimitReached?: () => void
}

export const ProjectBar: React.FC<ProjectBarProps> = ({
  maxProjects,
  onLimitReached,
}) => {
  const projects = useSequencerStore(s => s.projects)
  const currentProjectId = useSequencerStore(s => s.currentProjectId)
  const containers = useSequencerStore(s => s.containers)
  const currentContainerId = useSequencerStore(s => s.currentContainerId)
  const setActivePatternInBank = useSequencerStore(
    s => s.setActivePatternInBank,
  )
  const addPatternToBank = useSequencerStore(s => s.addPatternToBank)
  const duplicatePattern = useSequencerStore(s => s.duplicatePattern)
  const removePatternFromBank = useSequencerStore(s => s.removePatternFromBank)
  // Swing moved into QuantizePopover — no local slider binding here.
  const renameProject = useSequencerStore(s => s.renameProject)
  const pruneDuplicates = useSequencerStore(s => s.pruneDuplicates)
  const isPlaying = useSequencerStore(s => s.isPlaying)
  const queuedPatternId = useSequencerStore(s => s.queuedPatternId)
  const queuePatternSwitch = useSequencerStore(s => s.queuePatternSwitch)
  const cancelQueuedSwitches = useSequencerStore(s => s.cancelQueuedSwitches)
  const { features, tier } = useEffectiveFeatures()
  // Cloud-synced users need the dedupe to also hit the server,
  // otherwise the next hydrate brings the duplicates right back.
  const canCloudSync = features?.sequencerSave === true && tier !== 'free'

  // Patterns of the current bank — the tabs are scoped to one bank
  // so users can switch banks (via the BankSwitcher below) to see a
  // different pool of patterns.
  const activeBank = useMemo(() => {
    const container = containers[currentContainerId]
    if (!container) return null
    return container.banks[container.activeBankId] ?? null
  }, [containers, currentContainerId])

  const bankPatternIds = activeBank?.patternIds ?? []

  // Detect content-duplicate patterns within the active bank so we
  // can surface a "Dedupe" button only when it's actually needed.
  const duplicateCount = useMemo(() => {
    const seen = new Set<string>()
    let dupes = 0
    for (const pid of bankPatternIds) {
      const p = projects[pid]
      if (!p) continue
      const key = JSON.stringify({
        name: p.name,
        bpm: p.bpm,
        kitId: p.kitId,
        stepCount: p.stepCount,
        steps: p.steps,
        midiPads: p.midiPads,
        midiPadSteps: p.midiPadSteps,
      })
      if (seen.has(key)) dupes++
      else seen.add(key)
    }
    return dupes
  }, [bankPatternIds, projects])

  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const [libraryOpen, setLibraryOpen] = useState(false)

  const ordered = useMemo(() => {
    return bankPatternIds
      .map(pid => projects[pid])
      .filter((p): p is NonNullable<typeof p> => !!p)
      .sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { numeric: true }),
      )
  }, [bankPatternIds, projects])
  const count = ordered.length
  // Store-side hard cap at 16 per bank (see PR 5: Pattern mode maps
  // pads 1-16 to patterns 1-16). Tier-based `maxProjects` still
  // applies for lower tiers; we take the stricter of the two so the
  // UI greys the `+` button as soon as EITHER limit fires.
  const HARD_PATTERN_CAP = 16
  const effectiveLimit = Math.min(maxProjects, HARD_PATTERN_CAP)
  const atLimit = count >= effectiveLimit

  const handleAdd = () => {
    if (atLimit) {
      onLimitReached?.()
      return
    }
    addPatternToBank()
  }

  const handleDelete = (patternId: string) => {
    if (!activeBank) return
    removePatternFromBank(activeBank.id, patternId)
  }

  const handleDuplicate = (patternId: string) => {
    if (atLimit) {
      onLimitReached?.()
      return
    }
    duplicatePattern(patternId)
  }

  // ⌘D / Ctrl+D duplicates the active pattern. Skipped while focus is
  // inside a text input / textarea / contentEditable so the browser's
  // default "bookmark this page" binding stays overridden only when
  // we're in a musical context.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== 'd') return
      const target = e.target as HTMLElement | null
      const tag = target?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) {
        return
      }
      if (!currentProjectId) return
      e.preventDefault()
      if (count >= maxProjects) {
        onLimitReached?.()
        return
      }
      duplicatePattern(currentProjectId)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [currentProjectId, count, maxProjects, onLimitReached, duplicatePattern])

  const handleSelect = (patternId: string) => {
    if (!activeBank) return
    // Re-clicking the currently active pattern: cancel any queued
    // switch so the user can back out of a pending change.
    if (patternId === currentProjectId) {
      if (queuedPatternId) cancelQueuedSwitches()
      return
    }
    if (isPlaying) {
      // Toggle: clicking the already-queued pattern cancels the queue.
      if (
        queuedPatternId?.bankId === activeBank.id &&
        queuedPatternId.patternId === patternId
      ) {
        cancelQueuedSwitches()
      } else {
        queuePatternSwitch(activeBank.id, patternId)
      }
    } else {
      setActivePatternInBank(activeBank.id, patternId)
    }
  }

  const startRename = (id: string, currentName: string) => {
    setRenamingId(id)
    setRenameValue(currentName)
  }

  const commitRename = () => {
    if (renamingId && renameValue.trim()) {
      renameProject(renamingId, renameValue.trim())
    }
    setRenamingId(null)
  }

  return (
    // Don't wrap — this bar sits inside a fixed-height header strip
    // in SequencerPage so vertical wrapping would push the Song/Step
    // card down out of alignment with the Pad and FX columns. Too
    // many pattern tabs scroll horizontally via the parent's
    // overflow-x-auto instead.
    <div className="flex items-center gap-1 flex-nowrap min-w-0">
      <button
        type="button"
        onClick={() => setLibraryOpen(true)}
        title="Browse patterns + shared patterns"
        aria-label="Open pattern library"
        className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-mono uppercase tracking-[0.14em] text-content-tertiary border border-brd-subtle hover:border-[color:var(--accent-halo)] hover:text-accent transition-colors mr-1"
      >
        <LibraryIcon size={10} />
        Patterns
      </button>
      {/* Pattern-scoped swing was previously rendered here; the
          slider moved into the Quantize popover so every "timing
          feel" control lives in one place. See QuantizePopover. */}
      {duplicateCount > 0 && (
        <button
          type="button"
          onClick={() => {
            const msg =
              duplicateCount === 1
                ? '1 duplicate pattern found. Remove it?'
                : `${duplicateCount} duplicate patterns found. Remove them (keeping the active one)?`
            if (!window.confirm(msg)) return
            const removedIds = pruneDuplicates()
            if (removedIds.length === 0) return
            // Fire-and-forget DELETE calls for each removed id so the
            // server forgets them too. Without this the next hydrate
            // pulls every dupe right back in, which is exactly the
            // bug the user kept seeing after clicking this button.
            // We swallow 404s (already-deleted) and 403s (not the
            // owner — shared project, can't delete) silently because
            // neither matters for the "my library is tidy" outcome.
            if (canCloudSync) {
              void Promise.all(
                removedIds.map(id =>
                  apiClient
                    .delete(`/api/v1/sequencer/projects/${id}`)
                    .catch(() => null),
                ),
              )
            }
            window.setTimeout(() => {
              window.alert(
                `Removed ${removedIds.length} duplicate pattern${
                  removedIds.length === 1 ? '' : 's'
                }.`,
              )
            }, 0)
          }}
          title={`${duplicateCount} duplicate pattern${duplicateCount === 1 ? '' : 's'} detected — tap to clean up`}
          aria-label="Remove duplicate patterns"
          className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-mono uppercase tracking-[0.14em] text-warning border border-warning/40 hover:border-warning hover:bg-warning/10 transition-colors mr-1"
        >
          <DedupeIcon size={10} />
          Dedupe {duplicateCount}
        </button>
      )}
      {ordered.map(p => {
        const active = p.id === currentProjectId
        const queued =
          !!activeBank &&
          queuedPatternId?.bankId === activeBank.id &&
          queuedPatternId.patternId === p.id &&
          !active
        const isRenaming = renamingId === p.id
        return (
          <div key={p.id} className="relative group">
            {isRenaming ? (
              <input
                type="text"
                autoFocus
                value={renameValue}
                onChange={e => setRenameValue(e.target.value)}
                onBlur={commitRename}
                onKeyDown={e => {
                  if (e.key === 'Enter') commitRename()
                  if (e.key === 'Escape') setRenamingId(null)
                }}
                className="w-28 px-2 py-1 rounded-md border border-[color:var(--accent-halo)] bg-surface-sunken text-content-primary text-xs font-mono focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-halo)]"
              />
            ) : (
              <button
                type="button"
                aria-pressed={active}
                onClick={() => handleSelect(p.id)}
                onDoubleClick={() => startRename(p.id, p.name)}
                title={
                  queued
                    ? `${p.name} — queued for next downbeat (click again to cancel)`
                    : 'Click to select, double-click to rename'
                }
                className={`px-2.5 py-1 rounded-md text-xs font-mono transition-colors ${
                  active
                    ? 'bg-accent-muted text-accent border border-[color:var(--accent-halo)]'
                    : queued
                      ? 'bg-surface-sunken text-content-primary border border-[color:var(--accent-halo)] animate-pulse'
                      : 'bg-surface-sunken text-content-secondary border border-brd-subtle hover:border-brd-emphasis hover:text-content-primary'
                }`}
              >
                {p.name}
                {queued && (
                  <span
                    aria-hidden
                    className="ml-1 inline-block w-1.5 h-1.5 rounded-full bg-accent align-middle"
                  />
                )}
              </button>
            )}
            {active && !isRenaming && (
              <>
                {/* Share button removed — sharing is scoped to the
                    Project level now (see Plan Phase 3). Patterns
                    inherit their project's share link when the
                    parent project is shared. */}
                <button
                  type="button"
                  title={
                    atLimit
                      ? `Pattern cap reached (${maxProjects})`
                      : `Duplicate ${p.name}  (⌘D)`
                  }
                  aria-label={`Duplicate ${p.name}`}
                  onClick={() => handleDuplicate(p.id)}
                  disabled={atLimit}
                  className="absolute -bottom-1.5 -left-1.5 w-4 h-4 rounded-full bg-surface-raised text-content-secondary border border-brd-subtle text-[9px] leading-none opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity grid place-items-center hover:border-[color:var(--accent-halo)] hover:text-accent disabled:opacity-30 disabled:hover:border-brd-subtle disabled:hover:text-content-secondary"
                >
                  <DuplicateIcon size={7} />
                </button>
              </>
            )}
            {active && count > 1 && !isRenaming && (
              <button
                type="button"
                title={`Delete ${p.name}`}
                aria-label={`Delete ${p.name}`}
                onClick={() => {
                  const id = p.id
                  handleDelete(id)
                  // Server-side delete too — cloud sync is paused
                  // during the refactor but if a pattern row exists
                  // from earlier sessions, still clear it so a
                  // future re-enable doesn't resurrect it.
                  if (canCloudSync) {
                    void apiClient
                      .delete(`/api/v1/sequencer/projects/${id}`)
                      .catch(() => null)
                  }
                }}
                className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-destructive text-white text-[9px] leading-none opacity-0 group-hover:opacity-100 transition-opacity grid place-items-center"
              >
                ×
              </button>
            )}
          </div>
        )
      })}
      <button
        type="button"
        onClick={handleAdd}
        title={
          atLimit
            ? effectiveLimit < maxProjects
              ? `Cap of ${HARD_PATTERN_CAP} patterns per bank (SP-404-style)`
              : `Upgrade for more than ${maxProjects} pattern${
                  maxProjects === 1 ? '' : 's'
                }`
            : 'Add a new pattern'
        }
        aria-label="Add pattern"
        className={`w-6 h-6 rounded-md text-sm font-mono flex items-center justify-center transition-colors ${
          atLimit
            ? 'bg-surface-sunken text-content-tertiary border border-brd-subtle cursor-not-allowed opacity-60'
            : 'bg-surface-sunken text-content-secondary border border-brd-subtle hover:border-[color:var(--accent-halo)] hover:text-accent'
        }`}
      >
        +
      </button>
      {atLimit && (
        <span className="text-[10px] font-mono text-content-tertiary ml-1">
          {count} / {effectiveLimit}
        </span>
      )}
      <ProjectLibrary
        isOpen={libraryOpen}
        onClose={() => setLibraryOpen(false)}
      />
    </div>
  )
}
